export type SkillKind = "SKILL" | "INTEREST";
export type SkillSphere = "WORK" | "LIFE";
export type SkillGrowthDimensionId = "attr-career" | "attr-experience" | "attr-physique";

export interface SkillProgressSnapshot {
  level: number;
  level_title: string;
  level_floor_xp: number;
  next_level_xp: number;
  level_progress: number;
  xp_to_next_level: number;
}

export interface InferredSkillTemplate {
  sourceKey: string;
  title: string;
  category: string;
  sphere: SkillSphere;
  growthDimensionId: SkillGrowthDimensionId;
  description: string;
}

const levelBaseXp = 40;

export function xpRequiredForLevel(level: number) {
  const safeLevel = Math.max(0, Math.floor(level));
  return levelBaseXp * safeLevel * safeLevel;
}

function levelTitle(level: number) {
  if (level <= 0) return "尚未实践";
  if (level <= 2) return "起步";
  if (level <= 5) return "入门";
  if (level <= 9) return "稳定运用";
  if (level <= 14) return "熟练";
  if (level <= 19) return "深入";
  if (level <= 29) return "进阶";
  return "长期专精";
}

export function createSkillProgress(totalXp: number): SkillProgressSnapshot {
  const safeXp = Math.max(0, Math.floor(Number(totalXp) || 0));
  const level = Math.floor(Math.sqrt(safeXp / levelBaseXp));
  const floorXp = xpRequiredForLevel(level);
  const nextXp = xpRequiredForLevel(level + 1);
  const range = Math.max(1, nextXp - floorXp);
  return {
    level,
    level_title: levelTitle(level),
    level_floor_xp: floorXp,
    next_level_xp: nextXp,
    level_progress: Math.min(100, Math.max(0, Math.round(((safeXp - floorXp) / range) * 100))),
    xp_to_next_level: Math.max(0, nextXp - safeXp)
  };
}

const exerciseTemplates: Array<InferredSkillTemplate & { keywords: string[] }> = [
  {
    sourceKey: "exercise-swimming",
    title: "游泳",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过真实下水练习积累耐力、呼吸控制与泳姿熟练度。",
    keywords: ["游泳", "自由泳", "蛙泳", "泳池", "游了"]
  },
  {
    sourceKey: "exercise-running",
    title: "跑步",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过跑步事实积累心肺、配速与持续运动能力。",
    keywords: ["跑步", "慢跑", "晨跑", "夜跑", "跑了"]
  },
  {
    sourceKey: "exercise-strength",
    title: "力量训练",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过力量训练积累动作质量、负重能力与身体控制。",
    keywords: ["力量训练", "健身", "举铁", "卧推", "深蹲", "硬拉"]
  },
  {
    sourceKey: "exercise-cycling",
    title: "骑行",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过真实骑行积累耐力、路线判断与骑行控制。",
    keywords: ["骑行", "骑自行车", "单车训练"]
  },
  {
    sourceKey: "exercise-hiking",
    title: "徒步",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过徒步事实积累耐力、步行距离与户外适应。",
    keywords: ["徒步", "爬山", "登山", "远足"]
  },
  {
    sourceKey: "exercise-yoga",
    title: "瑜伽",
    category: "运动",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过瑜伽练习积累柔韧、呼吸与身体控制。",
    keywords: ["瑜伽", "拉伸训练"]
  },
  {
    sourceKey: "exercise-badminton",
    title: "羽毛球",
    category: "球类",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过对打与训练积累步法、反应和击球控制。",
    keywords: ["羽毛球"]
  },
  {
    sourceKey: "exercise-basketball",
    title: "篮球",
    category: "球类",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过训练或比赛积累体能、球感与协作判断。",
    keywords: ["篮球"]
  },
  {
    sourceKey: "exercise-football",
    title: "足球",
    category: "球类",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过训练或比赛积累体能、球感与场上判断。",
    keywords: ["足球", "踢球"]
  },
  {
    sourceKey: "exercise-table-tennis",
    title: "乒乓球",
    category: "球类",
    sphere: "LIFE",
    growthDimensionId: "attr-physique",
    description: "通过对打与训练积累反应、步法与控球能力。",
    keywords: ["乒乓球", "打乒乓"]
  }
];

const genericExercisePhrases = ["完成锻炼", "今日锻炼", "去锻炼", "锻炼了", "运动打卡", "运动完成"];

export function inferExerciseSkills(value: string): InferredSkillTemplate[] {
  const normalized = String(value ?? "").toLowerCase();
  if (!normalized.trim()) return [];
  const specific = exerciseTemplates.filter((template) =>
    template.keywords.some((keyword) => normalized.includes(keyword))
  );
  if (specific.length > 0) return specific.slice(0, 3).map(({ keywords: _keywords, ...template }) => template);
  if (!genericExercisePhrases.some((phrase) => normalized.includes(phrase))) return [];
  return [
    {
      sourceKey: "exercise-general",
      title: "日常锻炼",
      category: "运动",
      sphere: "LIFE",
      growthDimensionId: "attr-physique",
      description: "用于承接未注明具体运动类型的真实锻炼记录。"
    }
  ];
}

export function resolveSkillDefaults(input: {
  title: string;
  kind?: SkillKind;
  sphere?: SkillSphere;
  category?: string;
  growthDimensionId?: SkillGrowthDimensionId;
}) {
  const kind = input.kind ?? "SKILL";
  const combined = `${input.title} ${input.category ?? ""}`;
  const exercise = inferExerciseSkills(combined)[0];
  const sphere = kind === "INTEREST" ? "LIFE" : input.sphere ?? exercise?.sphere ?? "WORK";
  const growthDimensionId =
    input.growthDimensionId ??
    exercise?.growthDimensionId ??
    (sphere === "WORK" ? "attr-career" : "attr-experience");
  return { kind, sphere, growthDimensionId };
}
